import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { graySheetWhitesInDark, injectSkin, keepEditorTitle, registerSpanishMexico, rememberEditorTheme } from './skin';
import { OFFICE_NAME } from './icons';

// Archivos que se abren con el editor (el backend decide si es edición o solo lectura)
export const OFFICE_FILE_RE = /\.(docx|docm|xlsx|xlsm|pptx|pptm|doc|odt|rtf|xls|ods|ppt|odp|ppsx|pdf|xps|oxps|djvu|vsdx|vsdm|vssx|vssm|vstx|vstm)$/i;

/** Tipo de app del editor según la extensión (los PDF y diagramas no tienen tipo propio en Drive). */
export function officeKind(name, fallback = 'doc') {
  const n = (name || '').toLowerCase();
  if (/\.(pdf|xps|oxps|djvu)$/.test(n)) return 'pdf';
  if (/\.(vsdx|vsdm|vssx|vssm|vstx|vstm)$/.test(n)) return 'diagram';
  if (/\.(xlsx|xlsm|xls|ods)$/.test(n)) return 'sheet';
  if (/\.(pptx|pptm|ppt|odp|ppsx)$/.test(n)) return 'slides';
  return fallback;
}

const scriptLoads = {};

function loadDocsApi(documentServerUrl) {
  if (window.DocsAPI) return Promise.resolve();
  const src = `${documentServerUrl}web-apps/apps/api/documents/api.js`;
  if (!scriptLoads[src]) {
    scriptLoads[src] = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => (window.DocsAPI ? resolve() : reject(new Error('Editor no disponible')));
      script.onerror = () => {
        delete scriptLoads[src];
        script.remove();
        reject(new Error(`No se pudo cargar ${OFFICE_NAME}`));
      };
      document.body.appendChild(script);
    });
  }
  return scriptLoads[src];
}

const apiError = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/* O1 (auditoría 2026-10-09): el editor de HOJAS del motor 9.4.0.1 se cuelga en ~6–9 % de las aperturas en frío
   (`TextareaField.js` se declara sin dependencias y usa `Common.UI.BaseView.extend` antes de que exista). Se queda
   el esqueleto gris sin `onAppReady` ni `onError` y el portal esperaba para siempre. Ahora, si la interfaz no
   está lista a tiempo, o el motor lanza un error antes de estarlo, se avisa con `onLoadFailed` (OfficeShell
   reintenta solo una vez). */
export const VIGIA_CARGA_MS = 25000;
const ESPERA_TRAS_ERROR_MS = 3000;
const CONFIG_TIMEOUT_MS = 30000; // O6: sin tiempo límite, «Cargando…» podía quedarse para siempre

/** Avisa de los errores de JavaScript del motor (mismo origen). Devuelve la función que deja de escuchar. */
function escucharErroresDelMotor(iframe, alError) {
  let parado = false;
  let reloj = null;
  let win = null;
  const enError = () => alError();
  const enganchar = () => {
    if (parado) return;
    try {
      const w = iframe.contentWindow;
      if (!w || w.location.href === 'about:blank') throw new Error('sin documento');
      if (w === win) return;
      win = w;
      win.addEventListener('error', enError);
    } catch {
      reloj = setTimeout(enganchar, 30);
    }
  };
  enganchar();
  iframe.addEventListener('load', enganchar);
  return () => {
    parado = true;
    clearTimeout(reloj);
    iframe.removeEventListener('load', enganchar);
    try { win?.removeEventListener('error', enError); } catch { /* iframe ya destruido */ }
  };
}

/**
 * Editor de Drive con coedición en tiempo real.
 * Todos los que abren el mismo archivo reciben la misma document.key y entran a la misma sesión;
 * El motor guarda en Drive vía /api/office/callback.
 */
export default function OfficeEditor({
  fileId, dark, actionLink, layout, linkToken, invitadoId, onConfig, onReady, onStateChange, onUnavailable, onReload, onPick,
  onLoadFailed, onWarning,
}) {
  const hostRef = useRef(null);
  const editorRef = useRef(null);
  const handlers = useRef({});
  handlers.current = { onConfig, onReady, onStateChange, onUnavailable, onReload, onPick, onLoadFailed, onWarning };
  // El tema puede cambiar mientras carga: se lee el último al pedir la config y al crear el editor
  const darkRef = useRef(dark);
  darkRef.current = dark;

  useEffect(() => {
    const host = hostRef.current;
    const cleanups = [];
    let cancelled = false;
    let documentoListo = false;
    let appLista = false;
    let falloAvisado = false;
    let vigia = null;
    const falloDeCarga = (motivo) => {
      if (appLista || cancelled || falloAvisado) return;
      falloAvisado = true;
      clearTimeout(vigia);
      handlers.current.onLoadFailed?.(motivo);
    };
    cleanups.push(() => clearTimeout(vigia));
    let invitadoActual = invitadoId;   // al refrescar, el invitado nuevo conserva el animal que le tocó al abrir
    const editor = () => editorRef.current;
    // Lo que se pide a /office/config (al abrir y al refrescar). El tema, el último; la acción, solo al abrir.
    const paramsConfig = (alAbrir) => {
      // Sin mobile=true: sin licencia, el editor web móvil solo permite ver
      const params = { theme: darkRef.current ? 'dark' : 'light' };
      if (alAbrir && actionLink) params.action = actionLink;
      if (layout) params.layout = layout;
      // Enlace general: quien entra puede no tener cuenta; el papel lo pone el enlace
      if (linkToken) params.link = linkToken;
      // Quien ya tenía animal (recargó la página) lo conserva: el backend solo valida que el id sea de invitado
      if (linkToken && invitadoActual) params.invitado = invitadoActual;
      return params;
    };

    // Selector de Drive → objeto firmado por el backend → método del editor
    const pickFromDrive = async (kind, c, apply) => {
      const choice = await handlers.current.onPick?.(kind);
      if (!choice) return;
      try {
        const { data } = await api.post(`/office/select/${fileId}`, {
          kind, c, source: choice.source, source_file_id: choice.id,
        });
        apply(data);
      } catch (err) {
        toast.error(apiError(err, 'No se pudo usar ese archivo'));
      }
    };

    (async () => {
      try {
        const { data } = await api.get(`/office/config/${fileId}`, { params: paramsConfig(true), timeout: CONFIG_TIMEOUT_MS });
        invitadoActual = data.identidad_invitado?.id || invitadoActual;
        handlers.current.onConfig?.(data);
        vigia = setTimeout(() => falloDeCarga('tiempo'), VIGIA_CARGA_MS);
        await loadDocsApi(data.documentServerUrl);
        if (cancelled || !host) return;
        // DocsAPI reemplaza el placeholder por un iframe: se crea fuera del árbol de React
        rememberEditorTheme(darkRef.current);
        const holderId = `office-${fileId}-${Date.now()}`;
        const placeholder = document.createElement('div');
        placeholder.id = holderId;
        host.replaceChildren(placeholder);
        editorRef.current = new window.DocsAPI.DocEditor(holderId, {
          ...data.config,
          width: '100%',
          height: '100%',
          events: {
            onAppReady: () => {
              appLista = true;
              clearTimeout(vigia);
              const iframe = host.querySelector('iframe');
              if (iframe) cleanups.push(keepEditorTitle(iframe, data.config.document.title));
              handlers.current.onReady?.(iframe);
            },
            onDocumentStateChange: (e) => handlers.current.onStateChange?.(!!e?.data),
            onDocumentReady: () => { documentoListo = true; },
            // O9 (auditoría 2026-10-09): el motor avisa con una ventana que bloquea la cinta cuando Drive no recibe
            // un guardado (−331) o no contesta al abrir (−101). El portal no se enteraba; ahora lo dice y deja reintentar.
            onWarning: (e) => {
              console.warn(OFFICE_NAME, e?.data);
              handlers.current.onWarning?.(e?.data || {});
            },
            // D7 (auditoría 2026-10-05): un error al ABRIR dejaba la pantalla en blanco o con el aviso del motor y
            // nada más; ahora el portal ofrece su alternativa. Con el documento ya abierto, el motor enseña su propio
            // diálogo y sacar a la persona del editor le haría perder la sesión: ahí solo se anota.
            onError: (e) => {
              console.error(OFFICE_NAME, e?.data);
              if (!documentoListo && !cancelled) {
                handlers.current.onUnavailable?.(e?.data?.errorDescription || `${OFFICE_NAME} no pudo abrir el archivo`);
              }
            },
            // D7: la versión del archivo cambió con el editor abierto (alguien restauró una versión, o se volvió de una
            // suspensión larga). Sin este evento el motor 9.4 bloquea el editor con «recarga» y ese aviso no hace nada.
            onRequestRefreshFile: async () => {
              try {
                const { data: fresca } = await api.get(`/office/config/${fileId}`, { params: paramsConfig(false) });
                if (typeof editor()?.refreshFile !== 'function') throw new Error('sin refreshFile');
                editor().refreshFile(fresca.config);
              } catch {
                if (!cancelled) handlers.current.onReload?.();
              }
            },

            // Historial de versiones
            onRequestHistory: async () => {
              try {
                const res = await api.get(`/office/history/${fileId}`);
                editor()?.refreshHistory(res.data);
              } catch (err) {
                editor()?.refreshHistory({ error: apiError(err, 'No se pudo cargar el historial') });
              }
            },
            onRequestHistoryData: async (e) => {
              const version = e?.data;
              try {
                const res = await api.get(`/office/history/${fileId}/data`, { params: { version } });
                editor()?.setHistoryData(res.data);
              } catch (err) {
                editor()?.setHistoryData({ error: apiError(err, 'No se pudo abrir esa versión'), version });
              }
            },
            onRequestHistoryClose: () => handlers.current.onReload?.(),
            onRequestRestore: async (e) => {
              const version = e?.data?.version;
              try {
                await api.post(`/office/history/${fileId}/restore`, { version });
                toast.success(`Versión ${version} restaurada`);
                handlers.current.onReload?.();
              } catch (err) {
                toast.error(apiError(err, 'No se pudo restaurar la versión'));
              }
            },

            // Menciones en comentarios y enlaces directos a un comentario
            onRequestUsers: async (e) => {
              const c = e?.data?.c;
              try {
                const res = await api.get(`/office/users/${fileId}`, { params: { c } });
                let { users } = res.data;
                if (c === 'info' && Array.isArray(e.data.id)) users = users.filter((u) => e.data.id.includes(u.id));
                editor()?.setUsers({ c, users });
              } catch {
                editor()?.setUsers({ c, users: [] });
              }
            },
            onRequestSendNotify: (e) => {
              api.post(`/office/notify/${fileId}`, e?.data || {}).catch(() => toast.error('No se pudo avisar a las personas mencionadas'));
            },
            onMakeActionLink: (e) => {
              const url = `${window.location.origin}${window.location.pathname}?action=${encodeURIComponent(JSON.stringify(e?.data))}`;
              editor()?.setActionLink(url);
            },

            // Archivos de Drive dentro del editor
            onRequestInsertImage: (e) => pickFromDrive('image', e?.data?.c, (obj) => editor()?.insertImage(obj)),
            onRequestSelectDocument: (e) => pickFromDrive('document', e?.data?.c, (obj) => editor()?.setRequestedDocument(obj)),
            onRequestSelectSpreadsheet: (e) => pickFromDrive('spreadsheet', e?.data?.c, (obj) => editor()?.setRequestedSpreadsheet(obj)),
          },
        });
        const iframe = host.querySelector('iframe');
        if (iframe) {
          cleanups.push(registerSpanishMexico(iframe));
          cleanups.push(graySheetWhitesInDark(iframe));
          cleanups.push(injectSkin(iframe, { app: layout === 'app', lectura: layout === 'lectura' && !data.can_edit }));
          // Un error antes de que la interfaz esté lista suele ser el cuelgue de O1: se da un margen por si no lo era
          cleanups.push(escucharErroresDelMotor(iframe, () => {
            if (!appLista) setTimeout(() => falloDeCarga('error'), ESPERA_TRAS_ERROR_MS);
          }));
        }
      } catch (err) {
        if (cancelled) return;
        // Sin respuesta del servidor (red caída o tiempo agotado): reintentar, no mandar al editor clásico (O3, O6)
        if (!err.response && handlers.current.onLoadFailed) {
          falloDeCarga('red');
          return;
        }
        const detail = err.response?.data?.detail || err.message || `${OFFICE_NAME} no disponible`;
        handlers.current.onUnavailable?.(typeof detail === 'string' ? detail : `${OFFICE_NAME} no disponible`);
      }
    })();
    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
      try {
        editorRef.current?.destroyEditor?.();
      } catch {
        /* el iframe ya no existe */
      }
      editorRef.current = null;
      host?.replaceChildren();
    };
    // actionLink solo al abrir y el tema se cambia en caliente (OfficeShell): recrear el editor a media edición desconectaría al usuario
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  return <div ref={hostRef} className="zo-editor-host" data-testid="office-editor" />;
}
